import { useOutletContext } from "react-router-dom";
import { useEffect, useRef, useState } from 'react';
import { BulkAPINames, GroupNames, PAGE_TITLE_BASE, PageDisplayNames, PageNames } from '../../Assets/variables';
import { apiInfos } from '../../Assets/API/apiInfos';
import { parseApiSchemas, separateWords } from "../../Utils/contentTransformers";
import DataDictionaryItem from "../../Components/DataDictionaryItem";
import MiniSearch from "minisearch";
import { Marker } from "react-mark.js";
import { apisv1 } from "../../Assets/API/apisv1";
import { Helmet } from "react-helmet-async";

const DataDictionary = () => {
    const {setActivePageItem} = useOutletContext();
    const searchInputElement = useRef("");
    const searchClearButtonElement = useRef(null);
    const resultListElement = useRef(null);
    let fieldsById = {};
    let fieldsSet = new Set();
    const [results, setResults] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [searchIndex, setSearchIndex] = useState(null);
    let allFieldsRef = useRef([]);
    const [allFields, setAllFields] = useState(createAllFields());
    const corporateActionsOptionRef = useRef(true);
    const referenceDataOptionRef = useRef(true);

    const getActiveGroups = () => {
        const groups = [];

        if (corporateActionsOptionRef.current) {
            groups.push(GroupNames.CorporateActions);
        }

        if (referenceDataOptionRef.current) {
            groups.push(GroupNames.ReferenceData);
        }

        return groups;
    };
    
    useEffect(() => {
        setActivePageItem(PageNames.BulkAPIDataDictionary);
        allFieldsRef.current = createAllFields();
        setAllFields(allFieldsRef.current);
    }, []);

    useEffect(() => {
        const index = new MiniSearch({
            fields: ['name', 'apiFieldName', 'description', 'enumValues'],
            storeFields: ['api', 'name', 'apiFieldName', 'description', 'type', 'format', 'enumValues', 'group'],
            searchOptions: {
                combineWith: "AND",
                boost: {name: 3, apiFieldName: 2, description: 2, enumValues: 1},
                prefix: true,
                fuzzy: false
            }
        });

        index.addAll(allFieldsRef.current);

        setSearchIndex(index);
    }, []);

    const handleSearch = () => {
        const term = searchInputElement.current.value;
        const newResults = searchIndex?.search(term) ?? [];
        const allowedGroups = new Set(getActiveGroups());
        const filteredResults = newResults.filter((result) => allowedGroups.has(result.group));
        
        setInputValue(term);
        setResults(filteredResults);

        if (term.length > 0) {
            searchClearButtonElement.current.classList.add("tab-content__field-searchButton--shown");
            searchClearButtonElement.current.classList.remove("tab-content__field-searchButton--hidden");
        } else {
            searchClearButtonElement.current.classList.add("tab-content__field-searchButton--hidden");
            searchClearButtonElement.current.classList.remove("tab-content__field-searchButton--shown");
        }
    };

    // Clicking on clear button clears search
    const clearSearchField = () => {
        searchInputElement.current.value = '';
        searchInputElement.current.focus();
        searchClearButtonElement.current.classList.add("tab-content__field-searchButton--hidden");
        searchClearButtonElement.current.classList.remove("tab-content__field-searchButton--shown");
        setInputValue('');
        filterBySearchOptions();
        handleSearch();
    };

    const filterByCategory = (reference) => {
        reference.current = !reference.current;
        filterBySearchOptions();
        handleSearch();
    }

    function filterBySearchOptions() {
        let filteredResults = allFields;
        let filters = {
            group: []
        };

        filters.group = getActiveGroups();

        filteredResults = filteredResults.filter(field =>
            Object.entries(filters).every(([key, filterArray]) => (
                filterArray.includes(field[key])
            ))
        );

        allFieldsRef.current = filteredResults;
    }

    function sortAllFieldsInAscendingOrder(fieldsArray) {
        fieldsArray.sort((a, b) => {
            const nameA = a.title.toUpperCase(); // ignore upper and lowercase
            const nameB = b.title.toUpperCase(); // ignore upper and lowercase
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }

            // names must be equal
            return 0;
        });
    };

    function createAllFields() {
        let initAllFields = [];
        fieldsSet = new Set();
        Object.keys(BulkAPINames).forEach((apiName) => {
            let apiFieldsFromSchema = parseApiSchemas(apisv1[apiName], apiName);
            Object.keys(apiFieldsFromSchema).forEach((endPoint) => {
                let fieldsInAPIArray = [];
                let fieldsInAPIObject = {};

                Object.keys(apiFieldsFromSchema[endPoint]).forEach((apiField) => {
                    fieldsInAPIArray.push(apiFieldsFromSchema[endPoint][apiField]);
                });

                sortAllFieldsInAscendingOrder(fieldsInAPIArray);
                fieldsInAPIArray.forEach((field) => {
                    let apifield = {
                        title: field.title,
                        fieldName: field.fieldName,
                        description: field.description,
                        type: field.type,
                        format: field.format ? field.format : '',
                        enumValues: field.enum ? field.enum : []
                    };

                    fieldsInAPIObject[apifield.title] = apifield;
                })

                Object.keys(fieldsInAPIObject).forEach((apiField) => {
                    let fieldKey = `${apiName}_${apiField}`;
                    let property = fieldsInAPIObject[apiField];

                    if (!fieldsSet.has(fieldKey) && property.description !== undefined) {

                        let field = {
                            id: initAllFields.length,
                            api: apiName,
                            name: apiField,
                            apiFieldName: property.fieldName,
                            description: property.description,
                            type: property.type,
                            format: property.format ? property.format : '',
                            enumValues: property.enumValues ? property.enumValues : [],                            
                            group: apiInfos.filter((api) => api.apiName === apiName)[0].group
                        };

                        initAllFields.push(field);
                        fieldsById[field.id] = field;
                        fieldsSet.add(fieldKey);
                    }
                })
            });
        });

        return initAllFields
    };

    function convertToCSV(fieldsList) {
        const fieldsListwithHeader = [{
            api: "API Name",
            name: "Field Name",
            apiFieldName: "API Field Name",
            description: "Description",
            type: "Type",
            format: "Format",
            enumValues: "Enum"
        }].concat(fieldsList);

        return fieldsListwithHeader.map(field => {
            const { api, name, apiFieldName, description, type, format, enumValues } = field;
            let formattedApiName = api.charAt(0).toUpperCase() + api.slice(1);
            formattedApiName = separateWords(formattedApiName).join(' ');

            let formattedDescription = description.replace(/(\r\n|\n|\r)/gm, " ");

            return `${formattedApiName};${name};${apiFieldName};${formattedDescription};${type};${format};${enumValues}`;
        }).join('\n');
    }

    function downloadCSV() {
        var hiddenElement = document.createElement('a');
        hiddenElement.href = 'data:attachment/text,' + encodeURI(convertToCSV(allFields));
        hiddenElement.target = '_blank';
        hiddenElement.download = `Bulk API Data Dictionary - v1.csv`;
        hiddenElement.click();
    }

    return (
        <>
            <Helmet>
                <title>{`${PAGE_TITLE_BASE}${PageDisplayNames.BulkAPIDataDictionary}`}</title>
                <meta name="description" content={`${PageDisplayNames.BulkAPIDataDictionary} page of the SIX Web API Portal`} />
                <link rel="canonical" href={`/${PageNames.BulkAPIDataDictionary}`} />
            </Helmet>
            <div id="data-dictionary-container" className="data-dictionary-container">
                <header className="data-dictionary-header">
                    <span className="data-dictionary-title"><h1>Bulk API Data Dictionary</h1><span className="version-tag">{`(v1)`}</span></span>
                    <div className="data-dictionary-header__inner">
                        <div className="data-dictionary-header__searchbar">
                            <div className="searchbar-container">
                                <div id="search">
                                    <input
                                        id="searchbar"
                                        type="text"
                                        name="search"
                                        placeholder="Search"
                                        autoComplete="none"
                                        autoCorrect="none"
                                        autoCapitalize="none"
                                        spellCheck="false"
                                        ref={searchInputElement}
                                        value={inputValue}
                                        onChange={() => handleSearch()} />
                                    <button
                                        className="tab-content__field-searchButton tab-content__field-searchButton--hidden"
                                        id="clear"
                                        ref={searchClearButtonElement}
                                        onClick={() => clearSearchField()}>×</button>
                                </div>
                                <form id="advanced-options">
                                    <div className="search-group">
                                        <b>Category:</b>
                                        <label className="search-group__row">
                                            <input
                                                defaultChecked={corporateActionsOptionRef.current}
                                                type="checkbox"
                                                name={GroupNames.CorporateActions}
                                                value={GroupNames.CorporateActions}
                                                onChange={() => filterByCategory(corporateActionsOptionRef) } /> {GroupNames.CorporateActions}
                                        </label>
                                        <label className="search-group__row">
                                            <input
                                                defaultChecked={referenceDataOptionRef.current}
                                                type="checkbox"
                                                name={GroupNames.ReferenceData}
                                                value={GroupNames.ReferenceData}
                                                onChange={() => filterByCategory(referenceDataOptionRef)} /> {GroupNames.ReferenceData}
                                        </label>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <button id="download" className="six-button six-button--action" onClick={() => downloadCSV()}>Download</button>
                    </div>
                </header>
                <div id="result-list" ref={resultListElement}>
                    <Marker mark={searchInputElement.current.value}>
                        {searchInputElement.current.value
                            ? results.map((result, index) => <DataDictionaryItem result={result} key={index} />)
                            : allFieldsRef.current.map((result, index) => (
                                <DataDictionaryItem result={result} key={index} />
                        ))}
                    </Marker>
                </div>
            </div>
        </>
    );
};

export default DataDictionary;